import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Link as RouterLink, useLocation, Outlet, useNavigate } from 'react-router-dom';
import { styled, alpha } from '@mui/material/styles';
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Stack,
  Button,
} from '@mui/material';
import Iconify from '../../components/iconify';
import Logo from '../../components/logo/Logo';
import useResponsive from '../../hooks/useResponsive';
import navConfig from './navConfig';
import api from '../../api';
import { toast } from 'react-toastify';

const NAV_WIDTH = 260;
const HEADER_HEIGHT = 72;

const StyledRoot = styled('div')({
  display: 'flex',
  minHeight: '100vh',
  background: 'linear-gradient(180deg, #F7F4EF 0%, #EEF2F6 48%, #F8FAFC 100%)',
});

const Main = styled('div')(({ theme }) => ({
  flexGrow: 1,
  minWidth: 0,
  paddingTop: HEADER_HEIGHT + 16,
  paddingBottom: theme.spacing(4),
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  [theme.breakpoints.up('lg')]: {
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
  },
}));

function NavItem({ item, active }) {
  return (
    <ListItemButton
      component={RouterLink}
      to={item.path}
      selected={active}
      sx={{
        borderRadius: 1.5,
        mb: 0.5,
        '&.Mui-selected': {
          bgcolor: alpha('#0F172A', 0.08),
          color: '#0F172A',
        },
      }}
    >
      <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>{item.icon}</ListItemIcon>
      <ListItemText primary={item.title} primaryTypographyProps={{ textTransform: 'capitalize', fontWeight: 600 }} />
    </ListItemButton>
  );
}

NavItem.propTypes = {
  item: PropTypes.object.isRequired,
  active: PropTypes.bool,
};

function SideNav({ open, onClose }) {
  const { pathname } = useLocation();
  const isDesktop = useResponsive('up', 'lg');

  const content = (
    <Box sx={{ height: 1, display: 'flex', flexDirection: 'column', px: 2, py: 3 }}>
      <Box sx={{ mb: 3, px: 1 }}>
        <Logo />
        <Typography variant="subtitle2" sx={{ mt: 1.5, color: 'text.secondary' }}>
          Super Admin
        </Typography>
      </Box>
      <List disablePadding>
        {navConfig.map((item) => (
          <NavItem
            key={item.path}
            item={item}
            active={pathname === item.path || (item.path !== '/super-admin' && pathname.startsWith(item.path))}
          />
        ))}
      </List>
    </Box>
  );

  if (isDesktop) {
    return (
      <Drawer
        open
        variant="permanent"
        PaperProps={{
          sx: {
            width: NAV_WIDTH,
            borderRight: (theme) => `1px solid ${alpha(theme.palette.grey[500], 0.16)}`,
            bgcolor: alpha('#FFFFFF', 0.72),
            backdropFilter: 'blur(12px)',
          },
        }}
      >
        {content}
      </Drawer>
    );
  }

  return (
    <Drawer open={open} onClose={onClose} PaperProps={{ sx: { width: NAV_WIDTH } }}>
      {content}
    </Drawer>
  );
}

SideNav.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
};

export default function SuperAdminLayout() {
  const [openNav, setOpenNav] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isDesktop = useResponsive('up', 'lg');
  const user = JSON.parse(localStorage.getItem('user') || 'null');

  useEffect(() => {
    setOpenNav(false);
  }, [pathname]);

  const logout = () => {
    api
      .delete('/sessions')
      .catch(() => null)
      .finally(() => {
        localStorage.clear();
        navigate('/login');
        toast.success('Logged out');
      });
  };

  return (
    <StyledRoot>
      <AppBar
        position="fixed"
        color="transparent"
        elevation={0}
        sx={{
          width: { lg: `calc(100% - ${NAV_WIDTH}px)` },
          ml: { lg: `${NAV_WIDTH}px` },
          bgcolor: alpha('#FFFFFF', 0.7),
          backdropFilter: 'blur(14px)',
          borderBottom: (theme) => `1px solid ${alpha(theme.palette.grey[500], 0.12)}`,
        }}
      >
        <Toolbar sx={{ minHeight: `${HEADER_HEIGHT}px !important` }}>
          {!isDesktop && (
            <IconButton onClick={() => setOpenNav(true)} sx={{ mr: 1 }}>
              <Iconify icon="eva:menu-2-fill" />
            </IconButton>
          )}
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700 }}>
            Platform console
          </Typography>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
              {user?.email}
            </Typography>
            <Button size="small" variant="outlined" color="inherit" onClick={logout}>
              Logout
            </Button>
          </Stack>
        </Toolbar>
      </AppBar>

      <SideNav open={openNav} onClose={() => setOpenNav(false)} />

      <Main sx={{ width: { lg: `calc(100% - ${NAV_WIDTH}px)` }, ml: { lg: `${NAV_WIDTH}px` } }}>
        <Outlet />
      </Main>
    </StyledRoot>
  );
}
